import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Award,
  ArrowUpRight,
  Briefcase,
  CheckCircle,
  CircleCheck,
  CircleX,
  Clock,
  ClipboardList,
  Filter,
  Hourglass,
  ShieldCheck,
  ShieldX,
  TrendingUp,
  User,
  UserCheck,
  UserMinus,
  UserX,
  UserCog,
  UserPlus,
  UserRoundCheck,
  Users,
  Wallet,
  HandCoins,
  BadgeIndianRupee,
  Banknote,
  X,
  LineChart,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { SearchableSelect, SearchableSelectOption } from "../../components/ui/searchable-select";
import { Checkbox } from "../../components/ui/checkbox";
import { getAllUsers, getMyHmHrSubordinates, getMySubordinates } from "../../services/usersapi";
import {
  getReportSummary,
  getReportDailySeries,
  getReportEarningsBreakdown,
  addReportPinnedHm,
  removeReportPinnedHm,
  ReportSummary,
  ReportDailySeriesPoint,
  ReportEarningsBreakdownRow,
  ReportPinnedHmItem,
} from "../../services/reportApi";
import { ReportActivityChart, ReportChartModeToggle } from "../../components/report/ReportActivityChart";
import { getCurrentMonthDateRangeLocal } from "../../utils/dateRange";
import { useRbac } from "../../context";
import { toast } from "sonner";

const REPORT_ROLE_FILTER_MAP: Record<string, string[]> = {
  HM: ["HM", "AGENCY"],
  HR: ["HR"],
  SHM: ["SHM", "SHM_ADMIN"],
  FBDE: ["FBDE", "CONSULTANT"],
};

const getRoleOptionsForUser = (role?: string | null) => {
  const all = [
    { value: "all", label: "All Roles" },
    { value: "HM", label: "HM" },
    { value: "HR", label: "HR" },
    { value: "SHM", label: "SHM" },
    { value: "FBDE", label: "FBDE" },
  ] as const;

  switch (role) {
    case "ADMIN":
      return [...all];
    case "SHM":
    case "SHM_ADMIN":
      return [
        { value: "all", label: "All Roles" },
        { value: "SHM", label: "SHM" },
        { value: "HM", label: "HM" },
        { value: "HR", label: "HR" },
      ];
    case "FBDE":
    case "FBDE_ADMIN":
      return [
        { value: "all", label: "All Roles" },
        { value: "FBDE", label: "FBDE" },
        { value: "HM", label: "HM" },
        { value: "HR", label: "HR" },
      ];
    case "HM":
      return [
        { value: "all", label: "All Roles" },
        { value: "HM", label: "HM" },
        { value: "HR", label: "HR" },
      ];
    case "HR":
      return [
        { value: "all", label: "All Roles" },
        { value: "HR", label: "HR" },
      ];
    default:
      return [{ value: "all", label: "All Roles" }];
  }
};

const REPORT_METRIC_CONFIG = [
  {
    id: "subJobs",
    label: "Sub Jobs posts",
    key: "subJobs" as const,
    icon: Briefcase,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    chartColor: "#4f46e5",
  },
  {
    id: "approaches",
    label: "Total Approaches",
    key: "approaches" as const,
    icon: Users,
    color: "text-blue-600",
    bg: "bg-blue-50",
    chartColor: "#2563eb",
  },
  {
    id: "applications",
    label: "Total Applications",
    key: "applications" as const,
    icon: ClipboardList,
    color: "text-sky-600",
    bg: "bg-sky-50",
    chartColor: "#0284c7",
  },
  {
    id: "scheduled",
    label: "Total Scheduled",
    key: "scheduled" as const,
    icon: TrendingUp,
    color: "text-orange-600",
    bg: "bg-orange-50",
    chartColor: "#ea580c",
  },
  {
    id: "attended",
    label: "Total Attended",
    key: "attended" as const,
    icon: UserCheck,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    chartColor: "#059669",
  },
  {
    id: "interviewNotAttended",
    label: "Interview Not Attended",
    key: "interviewNotAttended" as const,
    icon: UserX,
    color: "text-red-700",
    bg: "bg-red-50",
    chartColor: "#b91c1c",
  },
  {
    id: "selections",
    label: "Total Selections",
    key: "selections" as const,
    icon: CheckCircle,
    color: "text-purple-600",
    bg: "bg-purple-50",
    chartColor: "#9333ea",
  },
  {
    id: "joins",
    label: "Total Joins",
    key: "joins" as const,
    icon: UserCheck,
    color: "text-green-600",
    bg: "bg-green-50",
    chartColor: "#16a34a",
  },
  {
    id: "notJoined",
    label: "Not Joined",
    key: "notJoined" as const,
    icon: UserMinus,
    color: "text-orange-700",
    bg: "bg-orange-50",
    chartColor: "#c2410c",
  },
  {
    id: "verified",
    label: "Total Verified",
    key: "verified" as const,
    icon: ShieldCheck,
    color: "text-green-700",
    bg: "bg-green-50",
    chartColor: "#15803d",
  },
  {
    id: "notVerified",
    label: "Total Not Verified",
    key: "notVerified" as const,
    icon: ShieldX,
    color: "text-red-600",
    bg: "bg-red-50",
    chartColor: "#dc2626",
  },
  {
    id: "pending",
    label: "Total Pending",
    key: "pending" as const,
    icon: Clock,
    color: "text-amber-600",
    bg: "bg-amber-50",
    chartColor: "#d97706",
  },
  {
    id: "approved",
    label: "Approved",
    key: "approved" as const,
    icon: CircleCheck,
    color: "text-teal-700",
    bg: "bg-teal-50",
    chartColor: "#0f766e",
  },
  {
    id: "notApproved",
    label: "Not Approved",
    key: "notApproved" as const,
    icon: CircleX,
    color: "text-rose-700",
    bg: "bg-rose-50",
    chartColor: "#e11d48",
  },
  {
    id: "approvalPending",
    label: "Approval Pending",
    key: "approvalPending" as const,
    icon: Hourglass,
    color: "text-yellow-700",
    bg: "bg-yellow-50",
    chartColor: "#ca8a04",
  },
  {
    id: "tenureClaimed",
    label: "Tenure Claimed",
    key: "tenureClaimed" as const,
    icon: Award,
    color: "text-violet-600",
    bg: "bg-violet-50",
    chartColor: "#7c3aed",
  },
] as const;

const ONBOARDING_METRIC_CONFIG = [
  {
    id: "onboardedHm",
    label: "Total Onboarded HM",
    key: "onboardedHm" as const,
    icon: UserCog,
    color: "text-fuchsia-600",
    bg: "bg-fuchsia-50",
  },
  {
    id: "onboardedHr",
    label: "Total Onboarded HR",
    key: "onboardedHr" as const,
    icon: UserPlus,
    color: "text-cyan-600",
    bg: "bg-cyan-50",
  },
  {
    id: "convertedHrToHm",
    label: "Converted HR to HM",
    key: "convertedHrToHm" as const,
    icon: ArrowUpRight,
    color: "text-lime-700",
    bg: "bg-lime-50",
  },
  {
    id: "activeHm",
    label: "Active HM",
    key: "activeHm" as const,
    icon: UserRoundCheck,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  {
    id: "activeHr",
    label: "Active HR",
    key: "activeHr" as const,
    icon: Users,
    color: "text-blue-700",
    bg: "bg-blue-50",
  },
] as const;

const EARNINGS_METRIC_CONFIG = [
  {
    id: "potentialEarning",
    label: "Potential Earning",
    hint: "Verified candidates",
    key: "potentialEarning" as const,
    icon: BadgeIndianRupee,
    color: "text-amber-700",
    bg: "bg-amber-50",
  },
  {
    id: "receivedEarning",
    label: "Received Earning",
    hint: "Tenure approved candidates",
    key: "receivedEarning" as const,
    icon: Banknote,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  {
    id: "payableEarning",
    label: "Payable Earning",
    hint: "Comm. Payable",
    key: "payableEarning" as const,
    icon: HandCoins,
    color: "text-orange-700",
    bg: "bg-orange-50",
  },
  // {
  //   id: "claimedEarning",
  //   label: "Claimed Earning",
  //   hint: "Comm. Claimed",
  //   key: "claimedEarning" as const,
  //   icon: Receipt,
  //   color: "text-violet-700",
  //   bg: "bg-violet-50",
  // },
] as const;

const REPORT_EARNINGS_ROLES = ["ADMIN", "SHM", "SHM_ADMIN", "FBDE_ADMIN"] as const;

const DEFAULT_GRAPH_METRICS = [
  "approaches",
  "scheduled",
  "attended",
  "selections",
  "joins",
] as const;

const EMPTY_SUMMARY: ReportSummary = {
  subJobs: 0,
  approaches: 0,
  applications: 0,
  scheduled: 0,
  attended: 0,
  interviewNotAttended: 0,
  selections: 0,
  joins: 0,
  notJoined: 0,
  verified: 0,
  notVerified: 0,
  pending: 0,
  approved: 0,
  notApproved: 0,
  approvalPending: 0,
  tenureClaimed: 0,
  onboardedHm: 0,
  onboardedHr: 0,
  convertedHrToHm: 0,
  activeHm: 0,
  activeHr: 0,
  potentialEarning: 0,
  receivedEarning: 0,
  payableEarning: 0,
  claimedEarning: 0,
};

/** UI-only placeholder — wire to API when HM role filter is active */
const HM_RECRUITER_EARNED_PLACEHOLDER = 124500;

const ReportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useRbac();
  const isAdmin = user?.role === "ADMIN";
  const showEarnings = REPORT_EARNINGS_ROLES.includes(
    (user?.role || "") as (typeof REPORT_EARNINGS_ROLES)[number]
  );
  const roleOptions = useMemo(() => getRoleOptionsForUser(user?.role), [user?.role]);
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedRecruiter, setSelectedRecruiter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [recruiterOptions, setRecruiterOptions] = useState<SearchableSelectOption[]>([
    { value: "all", label: "All Recruiters", searchText: "all recruiters" },
  ]);
  const [recruiterSearchTerm, setRecruiterSearchTerm] = useState("");
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false);
  const [summary, setSummary] = useState<ReportSummary>(EMPTY_SUMMARY);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [chartSeries, setChartSeries] = useState<ReportDailySeriesPoint[]>([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [isCumulativeGraph, setIsCumulativeGraph] = useState(false);
  const [graphSelectedMetrics, setGraphSelectedMetrics] = useState<Set<string>>(
    () => new Set(DEFAULT_GRAPH_METRICS)
  );
  const [showEarningsBreakdown, setShowEarningsBreakdown] = useState(false);
  const [earningsBreakdown, setEarningsBreakdown] = useState<ReportEarningsBreakdownRow[]>([]);
  const [isLoadingEarningsBreakdown, setIsLoadingEarningsBreakdown] = useState(false);
  const [earningsBreakdownRole, setEarningsBreakdownRole] = useState("all");
  const [pinnedHms, setPinnedHms] = useState<ReportPinnedHmItem[]>([]);
  const [hmPickerOptions, setHmPickerOptions] = useState<SearchableSelectOption[]>([]);
  const [hmPickerSearch, setHmPickerSearch] = useState("");
  const [selectedHmToAdd, setSelectedHmToAdd] = useState("");
  const [isLoadingHmPicker, setIsLoadingHmPicker] = useState(false);
  const [isSavingPinnedHm, setIsSavingPinnedHm] = useState(false);

  useEffect(() => {
    const role = searchParams.get("role");
    const recruiterId = searchParams.get("recruiterId");
    const from = searchParams.get("dateFrom");
    const to = searchParams.get("dateTo");

    if (role) setSelectedRole(role);
    if (recruiterId) setSelectedRecruiter(recruiterId);
    if (from) setDateFrom(from);
    if (to) setDateTo(to);
  }, [searchParams]);

  const buildReportFilterQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedRole !== "all") params.set("role", selectedRole);
    if (selectedRecruiter !== "all") params.set("recruiterId", selectedRecruiter);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    return params.toString();
  }, [selectedRole, selectedRecruiter, dateFrom, dateTo]);

  const handleMetricCardClick = useCallback(
    (metricKey: string) => {
      if (metricKey === "subJobs") return;
      const query = buildReportFilterQuery();
      navigate(`/report/metric/${metricKey}${query ? `?${query}` : ""}`);
    },
    [buildReportFilterQuery, navigate]
  );

  const hasActiveFilters = useMemo(
    () =>
      selectedRole !== "all" ||
      selectedRecruiter !== "all" ||
      Boolean(dateFrom) ||
      Boolean(dateTo),
    [selectedRole, selectedRecruiter, dateFrom, dateTo]
  );

  const clearFilters = () => {
    setSelectedRole("all");
    setSelectedRecruiter("all");
    setDateFrom("");
    setDateTo("");
  };

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    setSelectedRecruiter("all");
  };

  const showRecruiterEarned = selectedRole === "HM";

  const chartDateLabel = useMemo(() => {
    if (dateFrom && dateTo) {
      return `${dateFrom} to ${dateTo}`;
    }
    const { startDate, endDate } = getCurrentMonthDateRangeLocal();
    return `${startDate} to ${endDate}`;
  }, [dateFrom, dateTo]);

  const reportFilterParams = useMemo(
    () => ({
      recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
      role: selectedRole !== "all" ? selectedRole : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [selectedRecruiter, selectedRole, dateFrom, dateTo]
  );

  const chartMetrics = useMemo(
    () =>
      REPORT_METRIC_CONFIG.filter((metric) => graphSelectedMetrics.has(metric.key)).map(
        (metric) => ({
          key: metric.key,
          label: metric.label,
          color: metric.chartColor,
        })
      ),
    [graphSelectedMetrics]
  );

  const toggleGraphMetric = (key: string, checked: boolean) => {
    setGraphSelectedMetrics((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const allGraphMetricsSelected = useMemo(
    () => REPORT_METRIC_CONFIG.every((metric) => graphSelectedMetrics.has(metric.key)),
    [graphSelectedMetrics]
  );

  const toggleAllGraphMetrics = () => {
    if (allGraphMetricsSelected) {
      setGraphSelectedMetrics(new Set());
    } else {
      setGraphSelectedMetrics(new Set(REPORT_METRIC_CONFIG.map((metric) => metric.key)));
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);

  const filteredEarningsBreakdown = useMemo(() => {
    if (earningsBreakdownRole === "all") return earningsBreakdown;
    return earningsBreakdown.filter((row) => row.role === earningsBreakdownRole);
  }, [earningsBreakdown, earningsBreakdownRole]);

  const fetchEarningsBreakdown = useCallback(async () => {
    if (!isAdmin || !showEarningsBreakdown) return;
    setIsLoadingEarningsBreakdown(true);
    try {
      const response = await getReportEarningsBreakdown({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setEarningsBreakdown(response?.data?.rows ?? []);
      setPinnedHms(response?.data?.pinnedHms ?? []);
    } catch (error) {
      console.error("Failed to fetch earnings breakdown:", error);
      setEarningsBreakdown([]);
      setPinnedHms([]);
      toast.error("Failed to load earnings breakdown");
    } finally {
      setIsLoadingEarningsBreakdown(false);
    }
  }, [isAdmin, showEarningsBreakdown, dateFrom, dateTo]);

  useEffect(() => {
    fetchEarningsBreakdown();
  }, [fetchEarningsBreakdown]);

  const fetchHmPickerOptions = useCallback(async (search?: string) => {
    if (!isAdmin || !showEarningsBreakdown) return;
    setIsLoadingHmPicker(true);
    try {
      const [hmResponse, agencyResponse] = await Promise.all([
        getAllUsers({ role: "HM", search, limit: "50", blockStatus: false }),
        getAllUsers({ role: "AGENCY", search, limit: "50", blockStatus: false }),
      ]);
      const users = [
        ...(hmResponse?.data?.users ?? []),
        ...(agencyResponse?.data?.users ?? []),
      ];
      const pinnedSet = new Set(pinnedHms.map((item) => item.userId));
      const options: SearchableSelectOption[] = users
        .filter((user: { id: string }) => user.id && !pinnedSet.has(user.id))
        .map((user: { id: string; username?: string; email?: string; role?: string }) => ({
          value: user.id,
          label: `${user.username || user.email || "Unknown"}${user.role ? ` (${user.role})` : ""}`,
          searchText: `${user.username || ""} ${user.email || ""} ${user.role || ""}`.trim(),
        }));
      setHmPickerOptions(options);
    } catch (error) {
      console.error("Failed to load HM options:", error);
      setHmPickerOptions([]);
    } finally {
      setIsLoadingHmPicker(false);
    }
  }, [isAdmin, showEarningsBreakdown, pinnedHms]);

  useEffect(() => {
    if (!showEarningsBreakdown || !isAdmin) return;
    const timeout = setTimeout(() => {
      fetchHmPickerOptions(hmPickerSearch.trim() || undefined);
    }, hmPickerSearch.trim() ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [hmPickerSearch, fetchHmPickerOptions, showEarningsBreakdown, isAdmin]);

  const handleAddPinnedHm = async (userId: string) => {
    if (!userId) return;
    setIsSavingPinnedHm(true);
    try {
      const response = await addReportPinnedHm(userId);
      setPinnedHms(response?.data?.pinnedHms ?? []);
      setSelectedHmToAdd("");
      toast.success("HM added to breakdown");
      await fetchEarningsBreakdown();
    } catch (error) {
      console.error("Failed to pin HM:", error);
      toast.error("Failed to add HM");
    } finally {
      setIsSavingPinnedHm(false);
    }
  };

  const handleRemovePinnedHm = async (userId: string) => {
    setIsSavingPinnedHm(true);
    try {
      const response = await removeReportPinnedHm(userId);
      setPinnedHms(response?.data?.pinnedHms ?? []);
      toast.success("HM removed from breakdown");
      await fetchEarningsBreakdown();
    } catch (error) {
      console.error("Failed to unpin HM:", error);
      toast.error("Failed to remove HM");
    } finally {
      setIsSavingPinnedHm(false);
    }
  };

  const fetchRecruiters = useCallback(async (search?: string) => {
    setIsLoadingRecruiters(true);
    try {
      let users: Array<{ id: string; username?: string; email?: string; role?: string }> = [];

      if (isAdmin) {
        if (selectedRole === "all") {
          const response = await getAllUsers({
            search,
            limit: "100",
            blockStatus: false,
          });
          users = response?.data?.users ?? [];
        } else if (selectedRole === "HM") {
          const [hmResponse, agencyResponse] = await Promise.all([
            getAllUsers({ role: "HM", search, limit: "50", blockStatus: false }),
            getAllUsers({ role: "AGENCY", search, limit: "50", blockStatus: false }),
          ]);
          users = [...(hmResponse?.data?.users ?? []), ...(agencyResponse?.data?.users ?? [])];
        } else if (selectedRole === "FBDE") {
          const [fbdeResponse, consultantResponse] = await Promise.all([
            getAllUsers({ role: "FBDE", search, limit: "50", blockStatus: false }),
            getAllUsers({ role: "CONSULTANT", search, limit: "50", blockStatus: false }),
          ]);
          users = [
            ...(fbdeResponse?.data?.users ?? []),
            ...(consultantResponse?.data?.users ?? []),
          ];
        } else {
          const response = await getAllUsers({
            role: selectedRole,
            search,
            limit: "50",
            blockStatus: false,
          });
          users = response?.data?.users ?? [];
        }
      } else {
        const hmHrRole =
          selectedRole === "HM" || selectedRole === "HR" ? selectedRole : undefined;
        const [subsResponse, hmHrResponse] = await Promise.all([
          getMySubordinates({ excludeBlocked: true }),
          getMyHmHrSubordinates({
            limit: 100,
            search,
            role: hmHrRole,
          }).catch(() => null),
        ]);

        users = [
          {
            id: user?.id || user?.userId || "",
            username: user?.username || user?.name,
            email: user?.email,
            role: user?.role,
          },
          ...(subsResponse?.data?.subordinates ?? []),
          ...(hmHrResponse?.data?.subordinates ?? []),
        ].filter((entry) => entry.id);

        if (selectedRole !== "all") {
          const allowedRoles = REPORT_ROLE_FILTER_MAP[selectedRole] || [selectedRole];
          users = users.filter((entry) =>
            entry.role ? allowedRoles.includes(entry.role) : false
          );
        }

        if (search?.trim()) {
          const term = search.trim().toLowerCase();
          users = users.filter((entry) =>
            `${entry.username || ""} ${entry.email || ""}`.toLowerCase().includes(term)
          );
        }
      }

      const uniqueUsers = Array.from(
        new Map(users.map((entry) => [entry.id, entry])).values()
      );

      const options: SearchableSelectOption[] = [
        { value: "all", label: "All Recruiters", searchText: "all recruiters" },
        ...uniqueUsers.map((entry) => ({
          value: entry.id,
          label: entry.username || entry.email || "Unknown",
          searchText: `${entry.username || ""} ${entry.email || ""}`.trim(),
        })),
      ];

      setRecruiterOptions(options);
    } catch (error) {
      console.error("Failed to fetch recruiters:", error);
      setRecruiterOptions([{ value: "all", label: "All Recruiters", searchText: "all recruiters" }]);
      toast.error("Failed to load recruiters for filtering");
    } finally {
      setIsLoadingRecruiters(false);
    }
  }, [selectedRole, isAdmin, user]);

  const summaryRequestIdRef = useRef(0);

  const fetchSummary = useCallback(async () => {
    const requestId = ++summaryRequestIdRef.current;
    setIsLoadingSummary(true);
    try {
      const response = await getReportSummary(reportFilterParams);
      if (requestId !== summaryRequestIdRef.current) return;
      setSummary({ ...EMPTY_SUMMARY, ...(response?.data ?? {}) });
    } catch (error) {
      if (requestId !== summaryRequestIdRef.current) return;
      console.error("Failed to fetch report summary:", error);
      setSummary(EMPTY_SUMMARY);
      toast.error("Failed to load report summary");
    } finally {
      if (requestId === summaryRequestIdRef.current) {
        setIsLoadingSummary(false);
      }
    }
  }, [reportFilterParams]);

  const fetchChartSeries = useCallback(async () => {
    setIsLoadingChart(true);
    try {
      const response = await getReportDailySeries(reportFilterParams);
      setChartSeries(response?.data?.series ?? []);
    } catch (error) {
      console.error("Failed to fetch report chart:", error);
      setChartSeries([]);
      toast.error("Failed to load activity chart");
    } finally {
      setIsLoadingChart(false);
    }
  }, [reportFilterParams]);

  useEffect(() => {
    fetchRecruiters();
  }, [fetchRecruiters]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchRecruiters(recruiterSearchTerm.trim() || undefined);
    }, recruiterSearchTerm.trim() ? 300 : 0);

    return () => clearTimeout(timeout);
  }, [recruiterSearchTerm, fetchRecruiters]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (showGraph) {
      fetchChartSeries();
    }
  }, [showGraph, fetchChartSeries]);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-4 sm:px-6 sm:py-5">
        <h1 className="text-lg font-semibold text-gray-900 sm:text-xl">Report</h1>
        <p className="mt-1 text-xs text-gray-500 sm:text-sm">
          {isAdmin
            ? "Overview of recruitment activity across the platform"
            : "Overview of recruitment activity in your hierarchy"}
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-[#EDEEF1]/40 p-4 sm:p-6">
        <Card className="border border-gray-200 bg-white shadow-sm">
          <CardHeader className="border-b border-gray-100 bg-gray-50/80 pb-4">
            <div className="flex flex-col gap-4">
              <CardTitle className="flex items-center gap-2 text-base font-bold text-gray-900 sm:text-lg">
                {isAdmin ? "Platform summary" : "Hierarchy summary"}
                {isLoadingSummary && (
                  <Loader2 className="h-4 w-4 animate-spin text-[#EC6E51]" aria-hidden="true" />
                )}
              </CardTitle>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <Select value={selectedRole} onValueChange={handleRoleChange}>
                  <SelectTrigger className="h-10 w-full rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                    <div className="flex min-w-0 items-center gap-2">
                      <Filter className="h-4 w-4 shrink-0 text-gray-500" />
                      <span className="truncate">
                        <SelectValue placeholder="Role" />
                      </span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <SearchableSelect
                  options={recruiterOptions}
                  value={selectedRecruiter}
                  onValueChange={setSelectedRecruiter}
                  onSearchChange={setRecruiterSearchTerm}
                  placeholder="All Recruiters"
                  searchPlaceholder="Search recruiters..."
                  emptyText={isLoadingRecruiters ? "Loading recruiters..." : "No recruiters found"}
                  icon={<User className="h-4 w-4 text-gray-500" />}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                  searchable
                  disabled={isLoadingRecruiters}
                />

                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={dateTo || undefined}
                  className="h-10 w-full rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                  title="From Date"
                  placeholder="From Date"
                />

                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom || undefined}
                  className="h-10 w-full rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                  title="To Date"
                  placeholder="To Date"
                />

                {hasActiveFilters && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearFilters}
                    className="h-10 w-full rounded-lg border-gray-200 hover:bg-orange-50 sm:w-auto"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="py-6">
            {showGraph && (
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500 sm:text-sm">
                  Check metrics below to include them in the activity graph.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleAllGraphMetrics}
                  className="h-8 shrink-0 rounded-lg border-gray-200 text-xs"
                >
                  {allGraphMetricsSelected ? "Uncheck all" : "Check all"}
                </Button>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {REPORT_METRIC_CONFIG.map((metric) => {
                const Icon = metric.icon;
                const value = summary[metric.key];
                const isGraphSelected = graphSelectedMetrics.has(metric.key);
                const isClickable = metric.key !== "subJobs" && !isLoadingSummary;

                return (
                  <div
                    key={metric.id}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onClick={isClickable ? () => handleMetricCardClick(metric.key) : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleMetricCardClick(metric.key);
                            }
                          }
                        : undefined
                    }
                    className={`flex gap-4 rounded-xl border-2 bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${
                      showGraph && isGraphSelected ? "border-[#EC6E51]/40" : "border-gray-200"
                    } ${isClickable ? "cursor-pointer hover:border-[#EC6E51]/50" : ""} ${
                      isLoadingSummary ? "pointer-events-none" : ""
                    }`}
                  >
                    <div
                      className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${metric.bg}`}
                    >
                      <Icon className={`h-6 w-6 ${metric.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                      {isLoadingSummary ? (
                        <div
                          className="mt-2 h-8 w-24 animate-pulse rounded-md bg-gray-200 sm:h-9 sm:w-28"
                          aria-hidden="true"
                        />
                      ) : (
                        <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 sm:text-3xl">
                          {value.toLocaleString("en-IN")}
                        </p>
                      )}
                      {showGraph && (
                        <label
                          className="mt-3 flex cursor-pointer items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isGraphSelected}
                            onCheckedChange={(checked) =>
                              toggleGraphMetric(metric.key, checked === true)
                            }
                            disabled={isLoadingSummary}
                          />
                          <span className="text-xs text-gray-500">Show in graph</span>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <Separator className="my-8 bg-gray-200" />

            {showEarnings && (
              <>
                <div>
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Earnings</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Hierarchy-wise commission totals for the selected filters
                      </p>
                    </div>
                    {isAdmin && (
                      <Button
                        type="button"
                        variant={showEarningsBreakdown ? "default" : "outline"}
                        onClick={() => setShowEarningsBreakdown((prev) => !prev)}
                        className={
                          showEarningsBreakdown
                            ? "h-9 rounded-lg bg-[#EC6E51] text-white hover:bg-[#d85f44]"
                            : "h-9 rounded-lg border-gray-200"
                        }
                      >
                        {showEarningsBreakdown ? "Hide breakdown" : "Breakdown by SHM / FBDE Admin"}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {EARNINGS_METRIC_CONFIG.map((metric) => {
                      const Icon = metric.icon;
                      const value = summary[metric.key] ?? 0;

                      return (
                        <div
                          key={metric.id}
                          className={`flex gap-4 rounded-xl border-2 border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${
                            isLoadingSummary ? "pointer-events-none" : ""
                          }`}
                        >
                          <div
                            className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${metric.bg}`}
                          >
                            <Icon className={`h-6 w-6 ${metric.color}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                            <p className="text-xs text-gray-400">{metric.hint}</p>
                            {isLoadingSummary ? (
                              <div
                                className="mt-2 h-8 w-28 animate-pulse rounded-md bg-gray-200 sm:h-9 sm:w-32"
                                aria-hidden="true"
                              />
                            ) : (
                              <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 sm:text-3xl">
                                {formatCurrency(value)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {isAdmin && showEarningsBreakdown && (
                    <div className="mt-4 rounded-xl border border-gray-200 bg-white">
                      <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <SearchableSelect
                            options={hmPickerOptions}
                            value={selectedHmToAdd || undefined}
                            onValueChange={(value) => {
                              setSelectedHmToAdd(value);
                              void handleAddPinnedHm(value);
                            }}
                            onSearchChange={setHmPickerSearch}
                            placeholder={
                              isLoadingHmPicker || isSavingPinnedHm
                                ? "Loading HMs..."
                                : "Add HM to breakdown"
                            }
                            searchPlaceholder="Search HM..."
                            emptyText="No HMs found"
                            icon={<User className="h-4 w-4 text-gray-500" />}
                            className="h-9 w-full rounded-lg border-gray-200 sm:max-w-sm"
                            loading={isLoadingHmPicker || isSavingPinnedHm}
                            disabled={isSavingPinnedHm}
                            searchable
                          />
                          <Select
                            value={earningsBreakdownRole}
                            onValueChange={setEarningsBreakdownRole}
                          >
                            <SelectTrigger className="h-9 w-full rounded-lg border-gray-200 sm:w-[200px]">
                              <SelectValue placeholder="Filter role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All roles</SelectItem>
                              <SelectItem value="SHM">SHM</SelectItem>
                              <SelectItem value="SHM_ADMIN">SHM_ADMIN</SelectItem>
                              <SelectItem value="FBDE_ADMIN">FBDE_ADMIN</SelectItem>
                              <SelectItem value="HM">HM</SelectItem>
                              <SelectItem value="AGENCY">AGENCY</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {pinnedHms.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {pinnedHms.map((hm) => (
                              <button
                                key={hm.userId}
                                type="button"
                                onClick={() => void handleRemovePinnedHm(hm.userId)}
                                disabled={isSavingPinnedHm}
                                className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-800 hover:bg-orange-100 disabled:opacity-60"
                                title="Remove from breakdown"
                              >
                                {hm.username}
                                <span className="text-[10px] text-orange-500">({hm.role})</span>
                                <X className="h-3 w-3" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>User</TableHead>
                              <TableHead>Role</TableHead>
                              <TableHead className="text-right">Potential</TableHead>
                              <TableHead className="text-right">Received</TableHead>
                              <TableHead className="text-right">Payable</TableHead>
                              <TableHead className="text-right">Claimed</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {isLoadingEarningsBreakdown ? (
                              <TableRow>
                                <TableCell colSpan={6} className="py-8 text-center text-sm text-gray-500">
                                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                                  Computing time-aware earnings for each SHM / FBDE Admin…
                                </TableCell>
                              </TableRow>
                            ) : filteredEarningsBreakdown.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} className="py-8 text-center text-sm text-gray-500">
                                  No earnings found for these roles
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredEarningsBreakdown.map((row) => (
                                <TableRow key={row.userId}>
                                  <TableCell className="font-medium text-gray-900">
                                    <span className="inline-flex items-center gap-2">
                                      {row.username}
                                      {row.pinned && (
                                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">
                                          Pinned HM
                                        </span>
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-gray-600">{row.role}</TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {formatCurrency(row.potentialEarning)}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {formatCurrency(row.receivedEarning)}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {formatCurrency(row.payableEarning)}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums">
                                    {formatCurrency(row.claimedEarning)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>

                <Separator className="my-8 bg-gray-200" />
              </>
            )}

            <div>
              <p className="mb-4 text-sm font-semibold text-gray-800">Team onboarding</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {ONBOARDING_METRIC_CONFIG.map((metric) => {
                  const Icon = metric.icon;
                  const value = summary[metric.key];

                  return (
                    <div
                      key={metric.id}
                      className={`flex gap-4 rounded-xl border-2 border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${
                        isLoadingSummary ? "pointer-events-none" : ""
                      }`}
                    >
                      <div
                        className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${metric.bg}`}
                      >
                        <Icon className={`h-6 w-6 ${metric.color}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                        {isLoadingSummary ? (
                          <div
                            className="mt-2 h-8 w-20 animate-pulse rounded-md bg-gray-200 sm:h-9 sm:w-24"
                            aria-hidden="true"
                          />
                        ) : (
                          <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 sm:text-3xl">
                            {value.toLocaleString("en-IN")}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant={showGraph ? "outline" : "default"}
            onClick={() => setShowGraph((prev) => !prev)}
            disabled={isLoadingSummary}
            className={
              showGraph
                ? "h-10 rounded-lg border-gray-200"
                : "h-10 rounded-lg bg-[#EC6E51] text-white hover:bg-[#d85f44]"
            }
          >
            <LineChart className="mr-2 h-4 w-4" />
            {showGraph ? "Hide graph" : "Show graph"}
          </Button>
        </div>

        {showGraph && (
        <Card className="mt-4 border border-gray-200 bg-white shadow-sm">
          <CardHeader className="border-b border-gray-100 bg-gray-50/80 pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <CardTitle className="text-base font-bold text-gray-900 sm:text-lg">
                  Activity trend
                </CardTitle>
                <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                  {isCumulativeGraph ? "Cumulative" : "Daily"} trend for selected metrics
                  {chartDateLabel ? ` (${chartDateLabel})` : ""}
                </p>
              </div>
              {!isLoadingChart && chartSeries.length > 0 && chartMetrics.length > 0 && (
                <ReportChartModeToggle
                  cumulative={isCumulativeGraph}
                  onCumulativeChange={setIsCumulativeGraph}
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="py-6">
            {isLoadingChart ? (
              <div className="flex h-80 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/60">
                <Loader2 className="h-8 w-8 animate-spin text-[#EC6E51]" aria-hidden="true" />
                <p className="text-sm text-gray-500">Loading chart...</p>
              </div>
            ) : chartSeries.length === 0 ? (
              <div className="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/60">
                <p className="text-sm text-gray-500">No activity data for the selected range.</p>
              </div>
            ) : chartMetrics.length === 0 ? (
              <div className="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/60">
                <p className="text-sm text-gray-500">
                  Select at least one metric above to display on the graph.
                </p>
              </div>
            ) : (
              <ReportActivityChart
                series={chartSeries}
                selectedMetrics={chartMetrics}
                cumulative={isCumulativeGraph}
              />
            )}
          </CardContent>
        </Card>
        )}

        {showRecruiterEarned && (
          <Card className="mt-4 border border-gray-200 bg-white shadow-sm">
            <CardHeader className="border-b border-gray-100 bg-gray-50/80 pb-4">
              <CardTitle className="text-base font-bold text-gray-900 sm:text-lg">
                Recruiter earned
              </CardTitle>
              <p className="text-xs text-gray-500 sm:text-sm">
                Earnings summary for recruiters under the selected HM filters
              </p>
            </CardHeader>
            <CardContent className="py-6">
              <div className="flex max-w-md gap-4 rounded-xl border-2 border-[#EC6E51]/30 bg-[#FFF5F2] p-5 shadow-sm">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-orange-100">
                  <Wallet className="h-6 w-6 text-[#EC6E51]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-600">Total recruiter earned</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 sm:text-3xl">
                    {formatCurrency(HM_RECRUITER_EARNED_PLACEHOLDER)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
